<script lang="ts">
  import fileUtils from "@shared/lib/file_from_drop";
  import { CustomHttpPromiseWithEntireResponse } from "@shared/svelte/Modules/ApiService";
  import Flash from "@shared/services/flashv2.js";

  import type { Writable } from "svelte/store";
  import type { Field } from "svelte-forms2/types";

  export let imageIdField: Writable<Field<number>>;
  export let imageNameField: Writable<Field<string>>;
  export let onChange: (e?: Event) => void;
  export let helperText: string = "Image requirements: jpeg, jpg, png, gif, pdf";
  export let labelText: string = "Liquor license image";
  export let imageType: string = "image_upload";

  let loading: boolean = false;
  let fileData: File = null;
  let dragging = false;

  function startDragging() {
    dragging = true;
  }
  function stopDragging() {
    dragging = false;
  }

  const removeImage = async () => {
    loading = true;

    const newFieldStates: Field<any>[] = [
      { ...$imageIdField, value: null },
      { ...$imageNameField, value: null },
    ];
    await imageIdField.update(state => ({ ...state, ...newFieldStates[0] }));
    await imageNameField.update(state => ({ ...state, ...newFieldStates[1] }));

    loading = false;
    onChange();
  };

  const save = async () => {
    if (!fileData) {
      return (loading = false);
    }
    loading = true;

    let formData: FormData = new FormData();
    formData.append("image_upload[file]", fileData);
    formData.append("image_upload[entity_type]", "LiquorLicense");
    formData.append("image_upload[required_file_type]", "jpeg, jpg, png, gif, pdf");
    formData.append("image_upload[validate_file_type]", "true");
    formData.append(
      "image_upload[required_file_content_types]",
      "image/jpeg, image/jpg, image/png, image/gif, application/pdf"
    );
    formData.append("image_upload[validate_file_content_type]", "true");
    formData.append("image_upload[validate_file_content_type_matches_extension]", "true");

    const response = await CustomHttpPromiseWithEntireResponse({
      method: "POST",
      url: "/api/retailer/image_uploads",
      headers: {
        "Content-Type": undefined,
      },
      data: formData,
    });

    const data = await response.json();
    loading = false;

    if (response.ok) {
      $imageIdField.value = data.id;
      $imageNameField.value = fileData.name;
      fileData = null;
      onChange();
    } else {
      if (response.status === 415) {
        Flash.now({
          message: data.error[0],
          type: "error",
        });
      } else {
        Flash.now({
          message:
            "There was an error uploading your liquor license image. Please contact support.",
          type: "error",
        });
      }
    }
  };

  const uploadFromDrop = event => {
    stopDragging();
    fileData = fileUtils.getFilesFromDropEvent(event)[0];
    save();
  };

  const uploadFromBrowse = event => {
    stopDragging();
    fileData = event.target.files[0];
    save();
  };

</script>

<div class="fizz-stack-16" data-testid="liquor-license-{imageType}">
  <div class="fizz-space-between fizz-stack-8">
    <label class="fizz-label" for="image_upload">{labelText}</label>
    <button
      data-testid="remove-liquor-license-image-btn"
      class="fizz-button fizz-button-small"
      disabled={loading || !$imageIdField.value}
      on:click|preventDefault={removeImage}
    >
      Remove
    </button>
  </div>
  {#if loading}
    <div class="fizz-file-upload fizz-stack-8">Loading...</div>
  {:else}
    <label
      for="image-upload-input"
      class="fizz-file-upload fizz-stack-8 cursor-pointer"
      class:fizz-surface-blue={dragging}
      class:fizz-input-error={$imageIdField && !$imageIdField.valid}
      on:drop|preventDefault={uploadFromDrop}
      on:dragover|preventDefault={startDragging}
      on:dragleave|preventDefault={stopDragging}
    >
      <input
        type="file"
        data-testid="liquor-license-test-upload"
        on:change={uploadFromBrowse}
        id="image-upload-input"
      />
      {#if $imageNameField.value}
        <p>{$imageNameField.value}</p>
      {:else}
        <span>
          {`${dragging ? "Drop" : "Click"} to upload`}
        </span>
      {/if}
    </label>
    <p
      class="fizz-text-small-subdued"
      class:fizz-stack-16={!$imageIdField || $imageIdField.valid}
      style="white-space: pre-line"
    >
      {helperText}
    </p>
  {/if}
  <slot />
</div>
